import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useCurrency } from '../../context/CurrencyContext';

const storageKey = (visionId, month, index) =>
  `vision_strategy_${visionId}_m${month}_action_${index}`;

const ActionRow = ({ visionId, month, index, action, checked, onToggle, formatAmount }) => {
  const amount = action?.amount;
  return (
    <label
      className={`flex flex-wrap items-start gap-3 py-3 cursor-pointer group ${
        checked ? 'opacity-80' : ''
      }`}
    >
      <span
        className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded border-2 transition-colors duration-200 ${
          checked ? 'border-pine bg-pine' : 'border-pine bg-transparent'
        }`}
        aria-hidden
      >
        {checked && (
          <svg className="w-3 h-3 text-bone" viewBox="0 0 12 12" fill="none">
            <path
              d="M2 6l3 3 5-5"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </span>
      <input
        type="checkbox"
        className="sr-only"
        checked={checked}
        onChange={() => onToggle(month, index)}
      />
      <div className="flex-1 min-w-0">
        <span
          className={`text-body text-primary ${
            checked ? 'line-through text-ink-muted' : ''
          }`}
        >
          {action?.action}
        </span>
        <div className="flex flex-wrap gap-2 mt-1.5">
          {amount != null && Number(amount) > 0 && (
            <span className="inline-block px-2 py-0.5 rounded-md border border-hairline bg-pine-tint text-label text-pine figure tabular-nums">
              {formatAmount(amount)}
            </span>
          )}
          {action?.deadline && (
            <span className="inline-block px-2 py-0.5 rounded-md border border-hairline text-label text-ink-muted">
              {action.deadline}
            </span>
          )}
        </div>
      </div>
    </label>
  );
};

const VisionStrategyCard = ({ visionId, plan }) => {
  const { formatAmount } = useCurrency();
  const [activeMonth, setActiveMonth] = useState(1);
  const [checked, setChecked] = useState({});

  const months = useMemo(() => {
    const list = plan?.months || [];
    return [1, 2, 3].map((n) => list.find((m) => m.month === n) || list[n - 1] || null);
  }, [plan]);

  const loadChecked = useCallback(() => {
    if (!visionId) return;
    const state = {};
    for (let m = 1; m <= 3; m += 1) {
      for (let i = 0; i < 3; i += 1) {
        const key = storageKey(visionId, m, i);
        state[`${m}-${i}`] = localStorage.getItem(key) === '1';
      }
    }
    setChecked(state);
  }, [visionId]);

  useEffect(() => {
    loadChecked();
  }, [loadChecked, plan]);

  const onToggle = (month, index) => {
    const key = `${month}-${index}`;
    const next = !checked[key];
    setChecked((prev) => ({ ...prev, [key]: next }));
    if (visionId) {
      localStorage.setItem(storageKey(visionId, month, index), next ? '1' : '0');
    }
  };

  const current = months[activeMonth - 1];
  const doneCount = [0, 1, 2].filter((i) => checked[`${activeMonth}-${i}`]).length;

  if (!plan) return null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h4 className="text-body font-medium text-primary mb-1">3-Month Plan</h4>
        <p className="figure font-display text-h2 text-pine tabular-nums">
          {formatAmount(plan.monthly_commitment)}/month committed
        </p>
        {plan.overview && (
          <p className="text-body text-primary mt-3 leading-relaxed">{plan.overview}</p>
        )}
      </div>

      {/* Month tabs */}
      <div className="flex gap-1 overflow-x-auto pb-1 -mx-1 px-1 border-b border-hairline">
        {[1, 2, 3].map((m) => (
          <button
            key={m}
            type="button"
            onClick={() => setActiveMonth(m)}
            className={`shrink-0 px-4 py-2 text-body font-medium transition-colors duration-200 border-b-2 -mb-px ${
              activeMonth === m
                ? 'border-pine text-pine'
                : 'border-transparent text-ink-muted hover:text-primary'
            }`}
          >
            Month {m}
          </button>
        ))}
      </div>

      {current && (
        <div>
          <h5 className="text-body font-medium text-primary text-lg mb-2">
            {current.title}
          </h5>
          {current.focus && (
            <p className="text-body text-ink-muted italic mb-4">{current.focus}</p>
          )}

          <p className="text-label text-pine mb-3">
            {doneCount} of 3 actions done this month
          </p>

          <ul className="hairline-list divide-y divide-hairline">
            {(current.actions || []).slice(0, 3).map((action, idx) => (
              <li key={idx}>
                <ActionRow
                  visionId={visionId}
                  month={activeMonth}
                  index={idx}
                  action={action}
                  checked={!!checked[`${activeMonth}-${idx}`]}
                  onToggle={onToggle}
                  formatAmount={formatAmount}
                />
              </li>
            ))}
          </ul>

          {current.milestone && (
            <div className="mt-6 pl-4 border-l-2 border-pine">
              <p className="text-label text-ink-muted mb-1">
                Month {activeMonth} milestone:
              </p>
              <p className="text-body text-primary">{current.milestone}</p>
            </div>
          )}
        </div>
      )}

      {/* Aura verdict */}
      {plan.aura_verdict && (
        <div className="pt-4 border-t border-hairline">
          <span className="inline-block px-2.5 py-0.5 rounded-md border border-hairline bg-pine-tint text-label text-pine mb-3">
            Aura&apos;s Assessment
          </span>
          <p className="font-display text-body text-primary italic leading-relaxed text-lg">
            {plan.aura_verdict}
          </p>
        </div>
      )}

      {/* Risk */}
      {plan.risk && (
        <div className="pl-4 border-l-2 border-warning/60 bg-amber-50/50 py-3 pr-3 rounded-r-md">
          <p className="text-label text-warning font-medium mb-1">Watch out for:</p>
          <p className="text-body text-primary">{plan.risk}</p>
        </div>
      )}
    </div>
  );
};

export default VisionStrategyCard;
